import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ExerciseLogger from './components/ExerciseLogger';
import WorkoutProgress from './components/WorkoutProgress';
import SetLogEntry from './components/SetLogEntry';
import WorkoutSummaryModal from './components/WorkoutSummaryModal';

const WorkoutCompletionLogger = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, currentTenant, isAuthenticated, loading: authLoading } = useAuth();
  
  const [workoutSession, setWorkoutSession] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [setLogs, setSetLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSummary, setShowSummary] = useState(false);
  const [workoutMetrics, setWorkoutMetrics] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (user?.id && currentTenant?.id) {
      initializeWorkout();
    }
  }, [user, currentTenant]);

  const initializeWorkout = async () => {
    setLoading(true);
    try {
      const sessionId = location?.state?.sessionId;
      
      if (sessionId) {
        await loadExistingSession(sessionId);
      } else {
        await createNewSession();
      }
    } catch (err) {
      setError(err?.message || 'Failed to initialize workout');
    } finally {
      setLoading(false);
    }
  };

  const loadExistingSession = async (sessionId) => {
    const { data: session, error: sessionError } = await supabase
      ?.from('workout_sessions')
      ?.select('*')
      ?.eq('id', sessionId)
      ?.single();

    if (sessionError) throw sessionError;
    setWorkoutSession(session);

    const { data: exerciseData, error: exerciseError } = await supabase
      ?.from('exercise_logs')
      ?.select('*')
      ?.eq('workout_session_id', sessionId)
      ?.order('exercise_order');

    if (exerciseError) throw exerciseError;
    setExercises(exerciseData || []);

    if (exerciseData?.length > 0) {
      const { data: sets, error: setsError } = await supabase
        ?.from('set_logs')
        ?.select('*')
        ?.in('exercise_log_id', exerciseData?.map(e => e?.id))
        ?.order('set_number');

      if (setsError) throw setsError;
      setSetLogs(sets || []);
    }
  };

  const createNewSession = async () => {
    const workoutData = location?.state?.workoutData || {
      name: 'Custom Workout',
      exercises: []
    };

    const { data: session, error: sessionError } = await supabase
      ?.from('workout_sessions')
      ?.insert({
        tenant_id: currentTenant?.id,
        user_id: user?.id,
        workout_name: workoutData?.name,
        status: 'in_progress',
        total_exercises: workoutData?.exercises?.length || 0,
        total_sets: workoutData?.exercises?.reduce((sum, ex) => sum + (ex?.sets || 0), 0) || 0
      })
      ?.select()
      ?.single();

    if (sessionError) throw sessionError;
    setWorkoutSession(session);

    if (workoutData?.exercises?.length > 0) {
      const exerciseLogs = workoutData?.exercises?.map((ex, index) => ({
        workout_session_id: session?.id,
        exercise_name: ex?.name,
        exercise_order: index + 1,
        target_muscle: ex?.targetMuscle || '',
        difficulty: ex?.difficulty || 'intermediate',
        target_sets: ex?.sets || 3,
        target_reps: ex?.reps || 10,
        target_weight: ex?.weight || 0
      }));

      const { data: createdExercises, error: exerciseError } = await supabase
        ?.from('exercise_logs')
        ?.insert(exerciseLogs)
        ?.select();

      if (exerciseError) throw exerciseError;
      setExercises(createdExercises || []);
    }
  };

  const handleLogSet = async (exerciseLogId, setData) => {
    setLoading(true);
    setError('');

    try {
      const currentExercise = exercises?.find(e => e?.id === exerciseLogId);
      const currentSetNumber = setLogs?.filter(s => s?.exercise_log_id === exerciseLogId)?.length + 1;

      const { data: newSet, error: setError } = await supabase
        ?.from('set_logs')
        ?.insert({
          exercise_log_id: exerciseLogId,
          set_number: currentSetNumber,
          reps_completed: setData?.reps,
          weight_used: setData?.weight,
          rest_duration_seconds: setData?.restDuration || 60,
          difficulty_rating: setData?.difficultyRating || 5,
          form_notes: setData?.notes || ''
        })
        ?.select()
        ?.single();

      if (setError) throw setError;
      setSetLogs(prev => [...prev, newSet]);

      const { data: updatedSession } = await supabase
        ?.from('workout_sessions')
        ?.select('*')
        ?.eq('id', workoutSession?.id)
        ?.single();

      if (updatedSession) {
        setWorkoutSession(updatedSession);
      }

      if (currentSetNumber >= currentExercise?.target_sets) {
        if (currentExerciseIndex < exercises?.length - 1) {
          setCurrentExerciseIndex(prev => prev + 1);
        }
      }
    } catch (err) {
      setError(err?.message || 'Failed to log set');
    } finally {
      setLoading(false);
    }
  };

  const handleSkipExercise = async () => {
    const currentExercise = exercises?.[currentExerciseIndex];
    if (!currentExercise) return;

    try {
      await supabase
        ?.from('exercise_logs')
        ?.update({ status: 'skipped' })
        ?.eq('id', currentExercise?.id);

      if (currentExerciseIndex < exercises?.length - 1) {
        setCurrentExerciseIndex(prev => prev + 1);
      } else {
        await handleCompleteWorkout();
      }
    } catch (err) {
      setError(err?.message || 'Failed to skip exercise');
    }
  };

  const handleCompleteWorkout = async () => {
    setLoading(true);
    try {
      const completedAt = new Date()?.toISOString();
      const startedAt = new Date(workoutSession?.started_at);
      const durationSeconds = Math.floor((new Date(completedAt) - startedAt) / 1000);

      await supabase
        ?.from('workout_sessions')
        ?.update({
          status: 'completed',
          completed_at: completedAt,
          total_duration_seconds: durationSeconds
        })
        ?.eq('id', workoutSession?.id);

      await supabase?.rpc('calculate_workout_metrics', {
        session_uuid: workoutSession?.id
      });

      const { data: metrics } = await supabase
        ?.from('workout_performance_metrics')
        ?.select('*')
        ?.eq('workout_session_id', workoutSession?.id)
        ?.single();

      setWorkoutMetrics(metrics);
      setShowSummary(true);
    } catch (err) {
      setError(err?.message || 'Failed to complete workout');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSummary = () => {
    setShowSummary(false);
    navigate('/member-dashboard');
  };

  const currentExercise = exercises?.[currentExerciseIndex];
  const currentExerciseSets = setLogs?.filter(s => s?.exercise_log_id === currentExercise?.id) || [];

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" className="animate-spin mx-auto mb-4" size={48} />
          <p className="text-muted-foreground">Loading workout...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-6 pb-24 md:pb-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
                {workoutSession?.workout_name || 'Workout Logger'}
              </h1>
              <p className="text-sm text-muted-foreground">
                Track your sets, reps, and performance
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/member-dashboard')}
              iconName="X"
            >
              Exit
            </Button>
          </div>

          {error && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2">
                <Icon name="AlertCircle" size={20} className="text-error" />
                <p className="text-sm text-error">{error}</p>
              </div>
            </div>
          )}

          <WorkoutProgress
            totalExercises={exercises?.length}
            completedExercises={workoutSession?.completed_exercises || 0}
            totalSets={workoutSession?.total_sets || 0}
            completedSets={workoutSession?.completed_sets || 0}
            duration={workoutSession?.total_duration_seconds || 0}
          />

          {currentExercise && (
            <div className="space-y-6 mt-6">
              <ExerciseLogger
                exercise={currentExercise}
                exerciseNumber={currentExerciseIndex + 1}
                totalExercises={exercises?.length}
                completedSets={currentExerciseSets?.length}
                onSkip={handleSkipExercise}
              />

              <SetLogEntry
                exercise={currentExercise}
                currentSetNumber={currentExerciseSets?.length + 1}
                previousSet={currentExerciseSets?.[currentExerciseSets?.length - 1]}
                onLogSet={(setData) => handleLogSet(currentExercise?.id, setData)}
                loading={loading}
              />

              {currentExerciseSets?.length > 0 && (
                <div className="bg-card border border-border rounded-lg p-4">
                  <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
                    Completed Sets
                  </h3>
                  <div className="space-y-2">
                    {currentExerciseSets?.map((set, index) => (
                      <div
                        key={set?.id}
                        className="flex items-center justify-between p-3 bg-muted rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-success/20 flex items-center justify-center">
                            <Icon name="Check" size={16} className="text-success" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground">
                              Set {set?.set_number}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {set?.weight_used} lbs × {set?.reps_completed} reps
                            </p>
                          </div>
                        </div>
                        {set?.difficulty_rating && (
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">Difficulty</p>
                            <p className="text-sm font-medium text-foreground">
                              {set?.difficulty_rating}/10
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {exercises?.length > 0 && currentExerciseIndex >= exercises?.length - 1 && (
            <div className="mt-6">
              <Button
                onClick={handleCompleteWorkout}
                className="w-full"
                size="lg"
                iconName="CheckCircle"
                disabled={loading}
              >
                Complete Workout
              </Button>
            </div>
          )}
        </div>
      </main>

      <MobileBottomNav />

      {showSummary && (
        <WorkoutSummaryModal
          session={workoutSession}
          metrics={workoutMetrics}
          exercises={exercises}
          setLogs={setLogs}
          onClose={handleCloseSummary}
        />
      )}
    </div>
  );
};

export default WorkoutCompletionLogger;